import { useEffect } from 'react'
import { router, useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { TextControl } from '@/frontend/features/Fde/FormControls'
import ParticipantProfileFields from '@/frontend/features/Fde/ParticipantProfileFields'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { DigitalPerson, FdeCopy, OrganizationPerson } from '@/frontend/features/Fde/types'

export type EditableParticipant = { source: 'stationzero'; person: OrganizationPerson } | { source: 'agent'; person: DigitalPerson }

interface Props { open: boolean; onOpenChange: (open: boolean) => void; projectId: string; target: EditableParticipant | null; copy: FdeCopy }

export default function ParticipantRoleDialog({ open, onOpenChange, projectId, target, copy }: Props) {
  const form = useForm({ role_name: '', job_title: '', description: '' })

  useEffect(() => {
    if (open && target) {
      form.setData({ role_name: target.person.project_role || '', job_title: target.person.job_title || '', description: target.person.description || '' })
      form.clearErrors()
    }
  }, [open, target?.person.id])

  if (!target) return null
  const isUnlinkedInternal = target.source === 'stationzero' && !target.person.project_member_id
  const internalUserId = target.source === 'stationzero' ? target.person.user_id : null
  const recordId = target.source === 'agent' ? target.person.id : target.person.project_member_id
  const path = target.source === 'agent' ? fdeRoutes.projectAgents : fdeRoutes.projectMembers
  const key = target.source === 'agent' ? 'project_agent' : 'project_member'
  const url = fdeProjectPath(recordId ? `${path}/${recordId}` : path, projectId)

  function submit() {
    form.transform(({ job_title, ...data }) => ({ [key]: { ...data, ...(target?.source === 'stationzero' ? { job_title } : {}), ...(isUnlinkedInternal ? { user_id: internalUserId } : {}) } }))
    const options = { preserveScroll: true, onSuccess: () => onOpenChange(false) }
    if (isUnlinkedInternal) form.post(url, options)
    else form.patch(url, options)
  }

  function remove() {
    if (!window.confirm(copy.people.confirm_remove)) return
    router.delete(url, { preserveScroll: true, onSuccess: () => onOpenChange(false) })
  }

  return <FdeEditorDialog open={open} onOpenChange={onOpenChange} title={target.person.name} closeLabel={copy.common.close} size="standard" footer={<>{!isUnlinkedInternal && <Button variant="destructive" className="sm:mr-auto" onClick={remove}>{copy.people.remove_from_project}</Button>}<Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || !form.data.role_name.trim()} onClick={submit}>{copy.common.save}</Button></>}>
      <TextControl label={copy.people.project_role} value={form.data.role_name} onChange={(event) => form.setData('role_name', event.target.value)} required />
      <ParticipantProfileFields copy={copy} agent={target.source === 'agent'} jobTitle={form.data.job_title} description={form.data.description} onTitleChange={(value) => form.setData('job_title', value)} onDescriptionChange={(value) => form.setData('description', value)} />
      {Object.values(form.errors)[0] && <p role="alert" className="text-sm text-destructive">{Object.values(form.errors)[0]}</p>}
  </FdeEditorDialog>
}
